import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight, Ruler, Triangle, AlertTriangle, CheckCircle2, Info, Lock,
  MessageCircle, Upload, X, Loader2, Send, MapPin, Truck,
  Hammer, Wrench, Stamp, Copy,
} from "lucide-react";
import { api } from "../lib/api";
import PublicSeoContent from "../components/PublicSeoContent";

const WHATSAPP_URL  = "https://wa.me/message/ZUCUOZBBR3AYD1";

const PRODUCT_TYPE_OPTIONS = [
  { value: "straight_rubber_ramp", label: "Straight Rubber Ramp" },
  { value: "ramp_with_wings",      label: "Rubber Ramp With Wings" },
  { value: "rubber_infill",        label: "Custom Rubber Infill" },
  { value: "rubber_step",          label: "Rubber Step / Platform" },
  { value: "timber_ramp",          label: "Timber Ramp" },
  { value: "timber_step",          label: "Timber Platform / Step" },
  { value: "timber_infill",        label: "Timber Infill" },
  { value: "timber_custom",        label: "Timber Ramp With Wings" },
  { value: "aluminium_threshold",  label: "Aluminium Threshold Ramp" },
  { value: "handrails",            label: "Handrails / Grab Rails" },
  { value: "other",                label: "Other" },
];

const GRADIENT_OPTIONS = [
  { value: "5",  label: "1 : 5",  desc: "Loading / steep transition (non-accessible)" },
  { value: "8",  label: "1 : 8",  desc: "Occupied wheelchair maximum (short)" },
  { value: "10", label: "1 : 10", desc: "Comfortable for most users (mid)" },
  { value: "14", label: "1 : 14", desc: "AS1428.1 compliant accessibility (recommended)" },
];

function fmtMm(n) { return Number.isFinite(n) ? `${Math.round(n)} mm` : "—"; }
function fmtDeg(deg) { return Number.isFinite(deg) ? `${deg.toFixed(2)}°` : "—"; }

function gradientGuidance(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return null;
  if (ratio >= 14) return { tone: "ok", msg: "Compliant with AS1428.1 (1:14 or shallower) — suitable for accessible use." };
  if (ratio >= 10) return { tone: "warn", msg: "Steeper than 1:14. Generally OK as a short transition but NOT AS1428.1-compliant for general accessibility." };
  if (ratio >= 8)  return { tone: "warn", msg: "Steep — only safe for short distances with assistance. Not for unassisted wheelchair use." };
  return { tone: "danger", msg: "Very steep (1:8 or steeper). Loading / non-accessible use only. Not safe for unassisted access." };
}

export default function PublicRampCalculator() {
  const [params] = useSearchParams();
  const isEmbed = params.get("embed") === "1";
  // ---- Depth Calculator state ----
  // Empty by default per user spec — no auto-filled values on page load.
  const [dRise, setDRise] = useState("");
  const [dWidth, setDWidth] = useState("");
  const [dRatio, setDRatio] = useState("14");
  const [dCalculated, setDCalculated] = useState(false);
  const [dError, setDError] = useState("");

  const depthCalc = useMemo(() => {
    const rise = Number(dRise) || 0;
    const ratio = Number(dRatio) || 0;
    const depth = rise * ratio;
    const angleRad = depth > 0 ? Math.atan(rise / depth) : 0;
    const angleDeg = (angleRad * 180) / Math.PI;
    return { depth, ratio, angleDeg };
  }, [dRise, dRatio]);

  const depthPrefill = useMemo(
    () => ({ rise: dRise, depth: depthCalc.depth, width: dWidth }),
    [dRise, depthCalc.depth, dWidth],
  );

  // ---- Gradient Calculator state ----
  const [gRise, setGRise] = useState("");
  const [gDepth, setGDepth] = useState("");
  const [gWidth, setGWidth] = useState("");
  const [gCalculated, setGCalculated] = useState(false);
  const [gError, setGError] = useState("");

  const gradientCalc = useMemo(() => {
    const rise = Number(gRise) || 0;
    const depth = Number(gDepth) || 0;
    const ratio = rise > 0 ? depth / rise : 0;
    const angleDeg = depth > 0 ? Math.atan(rise / depth) * (180 / Math.PI) : 0;
    return { ratio, angleDeg };
  }, [gRise, gDepth]);

  // Guidance only shown after Calculate is clicked.
  const dGuide = dCalculated ? gradientGuidance(depthCalc.ratio) : null;
  const gGuide = gCalculated ? gradientGuidance(gradientCalc.ratio) : null;

  const handleDepthCalculate = () => {
    if (!dRise || Number(dRise) <= 0) {
      setDError("Please enter the required measurements to calculate your ramp.");
      setDCalculated(false);
      return;
    }
    setDError("");
    setDCalculated(true);
  };
  const handleDepthReset = () => {
    setDRise(""); setDWidth(""); setDRatio("14");
    setDCalculated(false); setDError("");
  };

  const handleGradientCalculate = () => {
    if (!gRise || !gDepth || Number(gRise) <= 0 || Number(gDepth) <= 0) {
      setGError("Please enter the required measurements to calculate your ramp.");
      setGCalculated(false);
      return;
    }
    setGError("");
    setGCalculated(true);
  };
  const handleGradientReset = () => {
    setGRise(""); setGDepth(""); setGWidth("");
    setGCalculated(false); setGError("");
  };

  // ---- Custom Quote Form prefilled product ----
  const [qrType, setQrType] = useState("");

  // ---- Copy-result feedback ----
  const [copiedKey, setCopiedKey] = useState("");
  const copyResult = (key, text) => {
    try {
      navigator.clipboard?.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1800);
    } catch { /* clipboard not available — silent */ }
  };
  const scrollToQuote = () => {
    document.getElementById("custom-quote-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white text-[#0A1128]" data-testid="public-ramp-calculator-page">
      {/* TOP CONTACT BAR / INTERNAL HEADER / ACCENT STRIP — REMOVED.
          This page is embedded into WordPress (https://ezyaccess.com.au/ramp-calculator/)
          which provides its own site header, navigation and contact strip. Duplicating
          them here causes a stacked double-header on the live site. Staff login is still
          accessible by navigating directly to /quote-portal/login. */}

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-10 md:pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fda500]/10 border border-[#fda500]/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#fda500]" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#0A1128] font-bold">Free Access Planning Tool</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A1128]">
              Plan the Right Ramp <span className="text-[#254391]">with Confidence</span>
            </h1>
            <div className="w-16 h-1 bg-[#fda500] mt-6 rounded-full" />
            <p className="text-base md:text-lg text-slate-600 mt-6 leading-relaxed max-w-xl">
              Use our free ramp calculator to estimate rise, depth, width and gradient for safer access planning — built by the team that manufactures and installs ramps every day.
            </p>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2.5 mt-7 max-w-lg">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-[#254391] rounded-md text-xs font-semibold">
                <Stamp size={13} /> Australian-Made Solutions
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-[#fda500] rounded-md text-xs font-semibold">
                <Hammer size={13} /> Melbourne Manufacturing
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-[#254391] rounded-md text-xs font-semibold">
                <Truck size={13} /> Australia-Wide Supply
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-[#fda500] rounded-md text-xs font-semibold">
                <Wrench size={13} /> Custom Access Solutions
              </span>
            </div>
          </div>

          {/* Diagram */}
          <div className="lg:col-span-6 relative">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_12px_32px_rgba(10,17,40,0.08)] p-6 md:p-8 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#254391] via-[#fda500] to-[#254391]" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-3">Ramp Anatomy</div>
              <RampDiagram />
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATORS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-14 md:pb-20">
        <div className="mb-8 md:mb-10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-2">Calculators</div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A1128]">Plan your ramp in two steps</h2>
          <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl">Calculate the depth you need for a target gradient, or check the gradient of a ramp you already have.</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          {/* DEPTH CALC */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="depth-calculator-card">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#254391] text-white flex items-center justify-center"><Ruler size={16} /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#fda500] font-bold">Calculator A</div>
                <div className="text-base font-bold">Ramp Depth Calculator</div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">Enter rise &amp; preferred gradient — get the required ramp depth.</p>

              <NumField testid="depth-rise" label="Rise (mm)" hint="Vertical height the ramp must overcome" value={dRise} onChange={setDRise} step={5} placeholder="Enter rise height in mm" tooltip="The vertical height from the lower surface to the top of the step or threshold." />
              <NumField testid="depth-width" label="Width (mm)" hint="Ramp width (does not affect depth)" value={dWidth} onChange={setDWidth} step={50} placeholder="Enter ramp width in mm" tooltip="The clear walking/wheeling width of the ramp. Min 900 mm; 1000 mm is the comfortable accessibility default." />

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">Preferred Gradient</label>
                <div className="grid grid-cols-2 gap-2">
                  {GRADIENT_OPTIONS.map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => setDRatio(g.value)}
                      data-testid={`depth-ratio-${g.value}`}
                      className={`text-left px-3 py-2 rounded-md border text-xs transition ${
                        dRatio === g.value
                          ? "border-[#254391] bg-[#254391]/5 text-[#254391]"
                          : "border-gray-200 hover:border-[#254391] text-gray-700"
                      }`}
                    >
                      <div className="font-bold text-sm">{g.label}</div>
                      <div className="text-[10px] text-gray-500 leading-snug mt-0.5">{g.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleDepthCalculate}
                  data-testid="depth-calculate-btn"
                  className="flex-1 px-4 py-2.5 bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold text-sm rounded-md shadow-sm transition-colors"
                >
                  Calculate
                </button>
                <button
                  type="button"
                  onClick={handleDepthReset}
                  data-testid="depth-reset-btn"
                  className="px-4 py-2.5 border border-gray-300 hover:border-[#254391] hover:text-[#254391] text-gray-700 font-semibold text-sm rounded-md transition-colors"
                >
                  Reset
                </button>
              </div>

              {/* Validation error */}
              {dError && (
                <div data-testid="depth-error" className="ezy-fade-in bg-orange-50 border border-orange-200 text-orange-800 rounded-md px-3 py-2.5 text-xs flex items-start gap-2">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{dError}</span>
                </div>
              )}

              {/* Output — only after Calculate is clicked */}
              {dCalculated && (
              <div className="ezy-fade-in mt-4 bg-gradient-to-br from-[#F8FAFC] to-white border border-slate-200 rounded-xl p-5 ezy-card" data-testid="depth-results">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#254391] font-bold mb-3">Your Result</div>
                <OutputRow label="Required Depth" value={fmtMm(depthCalc.depth)} highlight testid="depth-result-depth" />
                <OutputRow label="Gradient Ratio" value={`1 : ${depthCalc.ratio}`} testid="depth-result-ratio" />
                <OutputRow label="Ramp Angle" value={fmtDeg(depthCalc.angleDeg)} testid="depth-result-angle" />
                <Width3D depth={depthCalc.depth} rise={Number(dRise) || 0} width={Number(dWidth) || 0} />
                {dGuide && <GuidanceBox tone={dGuide.tone} msg={dGuide.msg} testid="depth-guidance" />}
                <div className="mt-3 text-[12px] text-slate-600 italic flex items-start gap-2" data-testid="depth-next-step">
                  <Info size={13} className="flex-shrink-0 mt-0.5 text-[#254391]" />
                  <span>Next step: request a custom quote with these measurements — our team will confirm fit and pricing within one business day.</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200">
                  <button type="button" onClick={() => copyResult("d", `Ramp depth result: Rise ${dRise} mm at 1:${dRatio} → Required depth ${Math.round(depthCalc.depth)} mm · Width ${dWidth || "—"} mm · Angle ${depthCalc.angleDeg.toFixed(2)}°`)}
                    data-testid="depth-copy-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 hover:border-[#254391] hover:text-[#254391] text-gray-700 font-semibold text-xs rounded-md transition-colors">
                    <Copy size={13} /> {copiedKey === "d" ? "Copied!" : "Copy result"}
                  </button>
                  <button type="button" onClick={scrollToQuote}
                    data-testid="depth-get-quote-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold text-xs rounded-md shadow-sm transition-colors">
                    Get a Quote <ArrowRight size={12} />
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>

          {/* GRADIENT CALC */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="gradient-calculator-card">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#fda500] text-[#0A1128] flex items-center justify-center"><Triangle size={16} /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#254391] font-bold">Calculator B</div>
                <div className="text-base font-bold">Ramp Gradient Calculator</div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">Enter rise, depth and width to check your ramp's actual gradient and safety.</p>

              <NumField testid="grad-rise" label="Rise (mm)" hint="Vertical height" value={gRise} onChange={setGRise} step={5} placeholder="Enter rise height in mm" tooltip="Vertical step height the ramp covers." />
              <NumField testid="grad-depth" label="Depth (mm)" hint="Horizontal run length" value={gDepth} onChange={setGDepth} step={50} placeholder="Enter ramp depth in mm" tooltip="Horizontal floor distance the ramp will occupy from bottom edge to top landing." />
              <NumField testid="grad-width" label="Width (mm)" hint="Ramp width" value={gWidth} onChange={setGWidth} step={50} placeholder="Enter ramp width in mm" tooltip="Clear walking width. Min 900 mm; 1200 mm for two-way traffic." />

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleGradientCalculate}
                  data-testid="grad-calculate-btn"
                  className="flex-1 px-4 py-2.5 bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold text-sm rounded-md shadow-sm transition-colors"
                >
                  Calculate
                </button>
                <button
                  type="button"
                  onClick={handleGradientReset}
                  data-testid="grad-reset-btn"
                  className="px-4 py-2.5 border border-gray-300 hover:border-[#254391] hover:text-[#254391] text-gray-700 font-semibold text-sm rounded-md transition-colors"
                >
                  Reset
                </button>
              </div>

              {/* Validation error */}
              {gError && (
                <div data-testid="grad-error" className="ezy-fade-in bg-orange-50 border border-orange-200 text-orange-800 rounded-md px-3 py-2.5 text-xs flex items-start gap-2">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{gError}</span>
                </div>
              )}

              {/* Output — only after Calculate */}
              {gCalculated && (
              <div className="ezy-fade-in mt-4 bg-gradient-to-br from-[#F8FAFC] to-white border border-slate-200 rounded-xl p-5 ezy-card" data-testid="grad-results">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#254391] font-bold mb-3">Your Result</div>
                <OutputRow label="Gradient Ratio" value={`1 : ${gradientCalc.ratio.toFixed(2)}`} highlight testid="grad-result-ratio" />
                <OutputRow label="Ramp Angle" value={fmtDeg(gradientCalc.angleDeg)} testid="grad-result-angle" />
                <OutputRow label="Width" value={fmtMm(Number(gWidth) || 0)} testid="grad-result-width" />
                {gGuide && <GuidanceBox tone={gGuide.tone} msg={gGuide.msg} testid="grad-guidance" />}
                <div className="mt-3 text-[12px] text-slate-600 italic flex items-start gap-2" data-testid="grad-next-step">
                  <Info size={13} className="flex-shrink-0 mt-0.5 text-[#254391]" />
                  <span>Next step: if the gradient is steeper than 1:14, talk to us about a longer ramp, a custom rubber infill, or an intermediate landing.</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200">
                  <button type="button" onClick={() => copyResult("g", `Gradient check: Rise ${gRise} mm over ${gDepth} mm = 1:${gradientCalc.ratio.toFixed(2)} · Angle ${gradientCalc.angleDeg.toFixed(2)}° · Width ${gWidth || "—"} mm`)}
                    data-testid="grad-copy-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 hover:border-[#254391] hover:text-[#254391] text-gray-700 font-semibold text-xs rounded-md transition-colors">
                    <Copy size={13} /> {copiedKey === "g" ? "Copied!" : "Copy result"}
                  </button>
                  <button type="button" onClick={scrollToQuote}
                    data-testid="grad-get-quote-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold text-xs rounded-md shadow-sm transition-colors">
                    Get a Quote <ArrowRight size={12} />
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA — hidden in embed mode (no staff login on public WP page) */}
      {!isEmbed && (
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20">
        <div className="relative overflow-hidden rounded-2xl bg-[#0A1128] text-white p-8 md:p-12">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#fda500] opacity-20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-[#254391] opacity-30 rounded-full blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-2">Quote Portal · Staff Only</div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight">
                Need an accurate ramp quote?
              </h2>
              <p className="text-sm md:text-base text-gray-300 mt-3 max-w-2xl leading-relaxed">
                Our public calculator helps estimate ramp size and gradient. For accurate pricing, fabrication
                and internal quoting, authorised Ezy Access Solutions staff can access the secure quoting portal.
              </p>
            </div>
            <Link
              to="/quote-portal/login"
              data-testid="staff-login-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold rounded-md transition shadow-lg whitespace-nowrap"
            >
              <Lock size={15} /> Staff Login / Quote Portal
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* PRODUCT CATEGORIES — REMOVED per SEO requirement: this page is the
          ramp calculator (SoftwareApplication), not a product listing. WooCommerce
          shop pages live at ezyaccess.com.au/shop/ and own their own product schema.
          Customers route to the custom quote form below. */}

      {/* CUSTOM QUOTE FORM */}
      <section id="custom-quote-form" className="bg-[#F8FAFC] border-y border-slate-200" data-testid="custom-quote-form-section">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="mb-8 text-center">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-2">Custom Quote Request</div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A1128]">Need a Custom Accessibility Solution?</h2>
            <p className="text-sm md:text-base text-slate-600 mt-3 max-w-2xl mx-auto">
              Send your measurements, photos or drawings and our team will recommend the safest and most suitable access solution for your space.
            </p>
          </div>
          <CustomQuoteForm
            initialType={qrType}
            depthPrefill={depthPrefill}
            onTypeChange={setQrType}
          />
        </div>
      </section>

      {/* TRUST SECTION — REMOVED per user request (Why Choose Us / badges).
          Keep page minimal and conversion-focused. */}

      {/* SEO SECTIONS - visible to users + indexable by Google */}
      {!isEmbed && <PublicSeoContent />}

      {/* DISCLAIMER */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-12" data-testid="disclaimer-section">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 md:p-5 flex items-start gap-3 text-xs md:text-[13px] text-[#0A1128]">
          <Info size={15} className="text-[#fda500] flex-shrink-0 mt-0.5" />
          <span>
            <strong>Disclaimer:</strong> Calculator results are estimates only and should be used as a guide. Final recommendations may vary depending on site conditions and Australian Standards compliance requirements.
          </span>
        </div>
      </section>

      {/* INTERNAL FOOTER REMOVED — WordPress provides its own site footer at
          https://ezyaccess.com.au/ramp-calculator/. Page ends cleanly after the
          disclaimer above so the embedded view has no duplicate footer spacing. */}

      {/* FLOATING WHATSAPP BUTTON */}
      {!isEmbed && (
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          aria-label="Chat with us on WhatsApp"
          data-testid="whatsapp-fab"
          className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-50 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-semibold px-4 py-3 rounded-full shadow-[0_10px_30px_rgba(37,211,102,0.45)] transition-transform hover:scale-105"
        >
          <MessageCircle size={18} />
          <span className="hidden sm:inline text-sm">Chat With Us</span>
        </a>
      )}
    </div>
  );
}

/* -------- Helpers -------- */
function NumField({ label, hint, value, onChange, step = 1, testid, placeholder, tooltip }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wider">
        <span>{label}</span>
        {tooltip && (
          <span title={tooltip} className="cursor-help text-[#254391] inline-flex" data-testid={`${testid}-tooltip`}>
            <Info size={11} />
          </span>
        )}
      </label>
      <input
        type="number"
        min={0}
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        data-testid={testid}
        className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm font-mono focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none transition"
      />
      {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function OutputRow({ label, value, highlight, testid }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
      <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
      <span data-testid={testid} className={`font-mono font-bold ${highlight ? "text-lg text-[#254391]" : "text-sm text-[#0A1128]"}`}>{value}</span>
    </div>
  );
}

function Width3D({ depth, rise, width }) {
  if (!depth || !rise) return null;
  return (
    <div className="text-[11px] text-gray-500 italic mt-1 mb-2">
      <Info size={11} className="inline mr-1" />
      Surface area ≈ {((depth * width) / 1_000_000).toFixed(2)} m² · Hypotenuse ≈ {Math.round(Math.sqrt(depth * depth + rise * rise))} mm
    </div>
  );
}

function GuidanceBox({ tone, msg, testid }) {
  const cfg = {
    ok: { bg: "bg-emerald-50", bd: "border-emerald-200", tx: "text-emerald-800", Ic: CheckCircle2 },
    warn: { bg: "bg-orange-50", bd: "border-orange-200", tx: "text-orange-800", Ic: AlertTriangle },
    danger: { bg: "bg-red-50", bd: "border-red-200", tx: "text-red-800", Ic: AlertTriangle },
  }[tone] || { bg: "bg-gray-50", bd: "border-gray-200", tx: "text-gray-700", Ic: Info };
  const Ic = cfg.Ic;
  return (
    <div data-testid={testid} className={`mt-3 ${cfg.bg} border ${cfg.bd} ${cfg.tx} rounded-md px-3 py-2.5 text-xs flex items-start gap-2 leading-relaxed`}>
      <Ic size={14} className="flex-shrink-0 mt-0.5" />
      <span>{msg}</span>
    </div>
  );
}

/* -------- SVG ramp diagram -------- */
function RampDiagram() {
  return (
    <svg viewBox="0 0 480 320" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" role="img" aria-label="Ramp diagram showing rise, depth and width">
      <defs>
        <linearGradient id="rampGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#254391" />
          <stop offset="100%" stopColor="#1E3675" />
        </linearGradient>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="480" height="320" fill="url(#grid)" />

      {/* Ground */}
      <line x1="40" y1="260" x2="440" y2="260" stroke="#0A1128" strokeWidth="2" />
      {/* Step / wall (rise) */}
      <rect x="350" y="160" width="60" height="100" fill="#cbd5e1" stroke="#475569" strokeWidth="1.5" />
      {/* Ramp body (3D-ish) */}
      <polygon points="80,260 350,260 350,160" fill="url(#rampGrad)" stroke="#0A1128" strokeWidth="1.5" />
      {/* Top side face for 3D depth */}
      <polygon points="80,260 110,240 380,240 350,260" fill="#1E3675" opacity="0.5" />
      <polygon points="350,160 380,140 380,240 350,260" fill="#1E3675" opacity="0.7" />
      <line x1="350" y1="160" x2="380" y2="140" stroke="#0A1128" strokeWidth="1" />

      {/* Labels */}
      {/* Depth */}
      <line x1="80" y1="285" x2="350" y2="285" stroke="#fda500" strokeWidth="2" markerStart="url(#arr)" markerEnd="url(#arr)" />
      <text x="215" y="305" textAnchor="middle" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="13" fill="#fda500">DEPTH</text>

      {/* Rise */}
      <line x1="425" y1="160" x2="425" y2="260" stroke="#fda500" strokeWidth="2" markerStart="url(#arr)" markerEnd="url(#arr)" />
      <text x="445" y="215" textAnchor="middle" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="13" fill="#fda500">RISE</text>

      {/* Width */}
      <line x1="80" y1="260" x2="110" y2="240" stroke="#fda500" strokeWidth="2" markerStart="url(#arr)" markerEnd="url(#arr)" />
      <text x="50" y="248" textAnchor="middle" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="13" fill="#fda500">WIDTH</text>

      {/* Arrow defs */}
      <defs>
        <marker id="arr" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="#fda500" />
        </marker>
      </defs>
    </svg>
  );
}


/* -------- Custom Quote Form -------- */
function CustomQuoteForm({ initialType = "", depthPrefill, onTypeChange }) {
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    phone: "",
    email: "",
    project_address: "",
    product_type: initialType,
    rise_mm: depthPrefill?.rise || "",
    width_mm: depthPrefill?.width || "",
    depth_mm: depthPrefill?.depth ? Math.round(depthPrefill.depth) : "",
    notes: "",
  });
  const [photos, setPhotos] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const photosRef = useRef(null);
  const drawingsRef = useRef(null);

  // Sync prop product type → form
  useEffect(() => {
    if (initialType && initialType !== form.product_type) {
      setForm((f) => ({ ...f, product_type: initialType }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialType]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "product_type") onTypeChange?.(v);
  };

  const addFiles = (current, setter, fileList, max = 5) => {
    const incoming = Array.from(fileList || []);
    if (current.length + incoming.length > max) {
      setErr(`Maximum ${max} files per category.`);
      return;
    }
    setErr("");
    setter([...current, ...incoming]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      photos.forEach((f) => fd.append("photos", f));
      drawings.forEach((f) => fd.append("drawings", f));
      const r = await api.post("/public/quote-request", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : (e.message || "Failed to submit. Please try again."));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-white border border-emerald-200 rounded-xl shadow-sm p-8 md:p-10 text-center" data-testid="custom-quote-success">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
          <CheckCircle2 size={28} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-1">Reference #{done.short_id}</div>
        <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Request received — thanks!</h3>
        <p className="text-sm text-slate-600 max-w-md mx-auto">
          Our team will review your details and get back to you within 1 business day.
          A confirmation email has been sent to <span className="font-mono">{form.email}</span>.
        </p>
        <button
          type="button"
          onClick={() => { setDone(null); setForm((f) => ({ ...f, notes: "" })); setPhotos([]); setDrawings([]); }}
          className="mt-6 px-4 py-2 text-sm border border-[#254391] text-[#254391] hover:bg-[#254391] hover:text-white rounded-md font-semibold transition-colors"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 md:p-8 space-y-5" data-testid="custom-quote-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Full Name" required>
          <input className="qr-inp" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="qr-full-name" autoComplete="name" />
        </Field>
        <Field label="Company Name">
          <input className="qr-inp" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} data-testid="qr-company" autoComplete="organization" />
        </Field>
        <Field label="Phone Number" required>
          <input className="qr-inp" required type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="qr-phone" autoComplete="tel" placeholder="(03) 7073 2121" />
        </Field>
        <Field label="Email Address" required>
          <input className="qr-inp" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="qr-email" autoComplete="email" placeholder="you@email.com.au" />
        </Field>
        <Field label="Project Address" full>
          <input className="qr-inp" value={form.project_address} onChange={(e) => update("project_address", e.target.value)} data-testid="qr-address" placeholder="Street, suburb, state, postcode" />
        </Field>
        <Field label="Product Type" full>
          <select className="qr-inp" value={form.product_type} onChange={(e) => update("product_type", e.target.value)} data-testid="qr-product-type">
            <option value="">— Select a product —</option>
            {PRODUCT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Field label="Rise / Height (mm)">
          <input className="qr-inp" type="number" min={0} value={form.rise_mm} onChange={(e) => update("rise_mm", e.target.value)} data-testid="qr-rise" />
        </Field>
        <Field label="Width (mm)">
          <input className="qr-inp" type="number" min={0} value={form.width_mm} onChange={(e) => update("width_mm", e.target.value)} data-testid="qr-width" />
        </Field>
        <Field label="Depth (mm)">
          <input className="qr-inp" type="number" min={0} value={form.depth_mm} onChange={(e) => update("depth_mm", e.target.value)} data-testid="qr-depth" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <FileDrop label="Upload Photos" hint="JPG, PNG, HEIC · max 5 files · 10 MB each" testid="qr-photos" inputRef={photosRef}
          accept="image/*" files={photos} setFiles={(fs) => addFiles(photos, setPhotos, fs)} onRemove={(i) => setPhotos(photos.filter((_, ix) => ix !== i))} />
        <FileDrop label="Upload Drawings" hint="PDF, JPG, PNG · max 5 files · 10 MB each" testid="qr-drawings" inputRef={drawingsRef}
          accept="image/*,application/pdf" files={drawings} setFiles={(fs) => addFiles(drawings, setDrawings, fs)} onRemove={(i) => setDrawings(drawings.filter((_, ix) => ix !== i))} />
      </div>

      <Field label="Notes" full>
        <textarea rows={4} className="qr-inp" value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="qr-notes" placeholder="Anything else we should know about your project?" />
      </Field>

      {err && (
        <div className="text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md flex items-start gap-2" data-testid="qr-error">
          <AlertTriangle size={13} className="mt-0.5" /> {err}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
        <p className="text-[11px] text-slate-500 max-w-md">
          By submitting, you agree to be contacted by Ezy Access Solutions about your enquiry. We never share your data.
        </p>
        <button type="submit" disabled={busy} data-testid="qr-submit"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#fda500] hover:bg-[#e59500] disabled:opacity-60 text-[#0A1128] font-bold rounded-md shadow-md transition-colors">
          {busy ? <><Loader2 size={15} className="animate-spin" /> Submitting…</> : <><Send size={15} /> Request Quote</>}
        </button>
      </div>

      <style>{`
        .qr-inp{width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;outline:none;transition:border .15s,box-shadow .15s;background:#fff}
        .qr-inp:focus{border-color:#254391;box-shadow:0 0 0 3px rgba(37,67,145,0.18)}
      `}</style>
    </form>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-[#fda500]">*</span>}
      </label>
      {children}
    </div>
  );
}

function FileDrop({ label, hint, accept, files, setFiles, onRemove, inputRef, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-700 mb-1.5">
        {label}
      </label>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); setFiles(e.dataTransfer.files); }}
        className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-[#254391] rounded-md p-4 text-center transition-colors bg-slate-50 hover:bg-blue-50/40"
        data-testid={`${testid}-drop`}
      >
        <input ref={inputRef} type="file" multiple accept={accept} className="hidden" onChange={(e) => { setFiles(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
        <Upload size={18} className="mx-auto text-slate-400 mb-1.5" />
        <div className="text-xs text-slate-600 font-semibold">Click to upload or drag &amp; drop</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
      </div>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {files.map((f, i) => (
            <li key={`${f.name}-${f.size}-${f.lastModified}`} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5">
              <span className="truncate text-slate-700 font-mono">{f.name}</span>
              <button type="button" onClick={() => onRemove(i)} className="text-red-500 hover:text-red-700 flex-shrink-0" aria-label={`Remove ${f.name}`} data-testid={`${testid}-remove-${i}`}>
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
